<template>
  <BaseModal @close="closeRepositionTool">
    <div class="modal-card">
      <div class="modal-card__header">
        <div class="modal-card__header__title">
          {{ $t('image.REPOSITION_IMAGE') }}
        </div>
        <ButtonClose
          :title="$t('buttons.CLOSE_WINDOW')"
          @click="closeRepositionTool()"
        />
      </div>
      <div class="modal-card__body">
        <div v-if="!isCropperReady" class="modal-card__body-cropper__loading">
          <LoadingSpinner>
            {{ $t('image.LOADING_IMAGE') }}
          </LoadingSpinner>
        </div>
        <Cropper
          ref="cropper"
          :class="[
            'modal-card__body-cropper',
            { 'modal-card__body-cropper--show': isCropperReady },
          ]"
          :src="cropperSourceImage"
          :stencil-props="stencilProps"
          @ready="showCropper"
          @change="updateCroppedImage"
        />
      </div>

      <div class="modal-card__footer">
        <BaseButton outline size="large" @click="closeRepositionTool()">
          {{ $t('buttons.CANCEL') }}
        </BaseButton>
        <BaseButton
          :loading="isApplyingCroppedImage"
          :disabled="isApplyingCroppedImage"
          theme="blue"
          size="large"
          @click="repositionImage()"
        >
          {{ $t('buttons.APPLY') }}
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/generics/base/BaseModal.vue'
import ButtonClose from '@/components/generics/ButtonClose.vue'
import LoadingSpinner from '@/components/generics/LoadingSpinner.vue'
import { isValidURL } from '@/helpers/validation'
import { queryParams } from '@attensi/utils'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import BaseButton from '../base/BaseButton.vue'

export default {
  components: {
    BaseButton,
    BaseModal,
    ButtonClose,
    Cropper,
    LoadingSpinner,
  },
  props: {
    previewImage: {
      type: String,
      required: true,
    },
    aspectRatio: {
      type: Number,
      required: true,
    },
  },
  emits: ['closeRepositionTool', 'repositionImage'],
  data() {
    return {
      isCropperReady: false,
      isApplyingCroppedImage: false,
      croppedImage: undefined,
    }
  },
  computed: {
    previewImagePreventCache() {
      const params = { preventCache: 'yes_please' }
      return queryParams(this.previewImage, params)
    },
    cropperSourceImage() {
      if (isValidURL(this.previewImage)) {
        return this.previewImagePreventCache
      } else {
        return this.previewImage
      }
    },
    stencilProps() {
      return {
        aspectRatio: this.aspectRatio,
      }
    },
  },
  methods: {
    showCropper() {
      this.isCropperReady = true
    },
    updateCroppedImage(data) {
      this.croppedImage = data
    },
    closeRepositionTool() {
      this.$emit('closeRepositionTool')
    },
    repositionImage() {
      this.$emit('repositionImage', this.croppedImage)
      this.isApplyingCroppedImage = true
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-card {
  display: flex;
  flex-direction: column;
  gap: $spacing-s;
  background: $color-canvas;
  padding: $spacing-m;
  max-width: 900px;
  min-width: 500px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__title {
      @include subtitle;
      color: $color-text;
    }
  }

  &__body-cropper {
    visibility: hidden;

    &__loading {
      @include subtitle-2;
      aspect-ratio: 16/9;
      display: flex;
      justify-content: center;
      align-items: center;
      color: $color-text-light;
      border-radius: $border-radius-s;
      background: $color-pebble;
    }

    &--show {
      visibility: initial;
    }
  }

  &__footer {
    @include form-action-buttons-layout;
  }
}
</style>

<style lang="scss">
@use '@/styles/cropper.scss' as *;
</style>
